import { useState } from 'react';
import { useCustom, useShow, useTranslate } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Button, Space, theme } from 'antd';
import type {
  PublicationDetailOut,
  PublicationDiffOut,
} from '@/api/generated/schemas';
import { ImportDiffModal } from '@/components/user_catalog/publications/ImportDiffModal';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { PUBLICATION_DEFAULT_TAB, PUBLICATION_SHOW_TABS } from '../constants';
import { PublicationModStatusEditButton } from '@/components/user_catalog/publications/PublicationModStatusEditButton';
import { PublicationShowHeader } from './PublicationShowHeader';
import { PublicationGeneralTab } from './PublicationGeneralTab';
import { PublicationAvailabilityTab } from './PublicationAvailabilityTab';
import { PublicationFormatsTab } from './PublicationFormatsTab';
import { PublicationReviewsTab } from './PublicationReviewsTab';
import { PublicationActivitiesTab } from './PublicationActivitiesTab';
import { SearchPopularityCard } from '@/components/common/show/SearchPopularityCard';

export const PublicationShow = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { query } = useShow<PublicationDetailOut>({
    resource: 'user_catalog.publications',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle('user_catalog.publications', record?.title);

  const [activeTab, onTabChange] = useShowTab(
    PUBLICATION_SHOW_TABS,
    PUBLICATION_DEFAULT_TAB
  );

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const hasUpstreamSource =
    record?.ebook_source_id != null || record?.audio_source_id != null;

  const { query: diffQuery } = useCustom<PublicationDiffOut>({
    url: `user_catalog/publications/${record?.id}/import-diff`,
    method: 'get',
    queryOptions: {
      enabled: !!record?.id && hasUpstreamSource,
    },
  });

  const diffFields = diffQuery?.data?.data?.fields ?? [];
  const hasDiffs = diffFields.length > 0;

  return (
    <>
      <Show
        isLoading={isLoading}
        contentProps={bareShowContentProps}
        headerButtons={({ defaultButtons }) => (
          <Space>
            {record && (
              <PublicationModStatusEditButton
                publicationId={record.id}
                title={record.title}
                onSaved={() => query.refetch()}
              />
            )}
            {defaultButtons}
          </Space>
        )}
      >
        {hasDiffs && (
          <div
            style={{
              background: token.colorWarningBg,
              border: `1px solid ${token.colorWarningBorder}`,
              borderRadius: token.borderRadiusLG,
              padding: `10px ${token.padding}px`,
              marginBottom: token.margin,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: token.fontSizeSM,
            }}
          >
            <span style={{ color: token.colorWarningTextActive }}>
              <strong>
                {t('user_catalog.publications.import.banner_title')}
              </strong>{' '}
              {t('user_catalog.publications.import.banner_desc', {
                count: diffFields.length,
              })}
            </span>
            <Button
              size="small"
              type="primary"
              onClick={() => setIsImportModalOpen(true)}
            >
              {t('user_catalog.publications.import.banner_action')}
            </Button>
          </div>
        )}

        {record && (
          <>
            <PublicationShowHeader
              record={record}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
            <div style={{ marginTop: token.margin }}>
              {activeTab === 'general' && (
                <PublicationGeneralTab record={record} />
              )}
              {activeTab === 'availability' && (
                <PublicationAvailabilityTab record={record} />
              )}
              {activeTab === 'formats' && (
                <PublicationFormatsTab record={record} />
              )}
              {activeTab === 'reviews' && (
                <PublicationReviewsTab record={record} />
              )}
              {activeTab === 'search' && (
                <SearchPopularityCard
                  value={record.search_popularity}
                  description={t(
                    'user_catalog.publications.show.search.description'
                  )}
                />
              )}
              {activeTab === 'activities' && (
                <PublicationActivitiesTab record={record} />
              )}
            </div>
          </>
        )}
      </Show>
      {record?.id && (
        <ImportDiffModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          publicationId={record.id}
          onSuccess={() => query.refetch()}
        />
      )}
    </>
  );
};
